import { useState, useEffect } from 'react';
import { Check, X, Zap, Bot, Star, Shield, TrendingUp, Clock, Users, Sparkles, Tag } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

export default function PricingPage() {
  const { user, isAuthenticated, updateUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponValid, setCouponValid] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState({
    email: '',
    breeder_name: '',
    current_listings: 0,
    ai_interest: '',
    notes: ''
  });
  const [waitlistCounts, setWaitlistCounts] = useState({});

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
    loadWaitlistCounts();
  }, [searchParams]);

  const loadWaitlistCounts = async () => {
    try {
      const [premium, elite] = await Promise.all([
        fetch(`/api/waitlist/count/premium`).then(r => r.json()),
        fetch(`/api/waitlist/count/elite`).then(r => r.json())
      ]);
      setWaitlistCounts({ premium: premium.count, elite: elite.count });
    } catch (e) {
      console.error('Error loading waitlist counts:', e);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const result = await api.checkSubscriptionStatus(sessionId);
      if (result.status === 'completed') {
        toast.success('¡Suscripción activada con éxito!');
        updateUser({ subscription: result.plan_id });
      }
    } catch (e) {
      console.error('Error checking payment:', e);
    }
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponValid(null);
    try {
      const result = await api.validateCoupon(couponCode.trim());
      setCouponValid(result);
      toast.success(`Cupón "${result.name}" aplicado`);
    } catch (e) {
      setCouponValid(null);
      toast.error(formatApiError(e));
    } finally {
      setCouponLoading(false);
    }
  };

  const getDiscountedPrice = (originalPrice) => {
    if (!couponValid) return null;
    if (couponValid.percent_off) {
      return originalPrice * (1 - couponValid.percent_off / 100);
    }
    if (couponValid.amount_off) {
      return Math.max(0, originalPrice - couponValid.amount_off);
    }
    return null;
  };

  const handleSubscribe = async (planId) => {
    if (!isAuthenticated) {
      window.location.href = '/registro?role=seller';
      return;
    }

    setLoading(planId);
    try {
      const result = await api.createCheckout(planId, window.location.origin, couponValid?.id || null);
      window.location.href = result.url;
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(null);
    }
  };

  const openWaitlist = (planId) => {
    setSelectedPlan(planId);
    setWaitlistForm(prev => ({ ...prev, email: user?.email || '' }));
    setWaitlistOpen(true);
  };

  const handleWaitlistSubmit = async () => {
    if (!waitlistForm.email) {
      toast.error('Por favor ingresa tu email');
      return;
    }
    
    try {
      const response = await fetch(`/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...waitlistForm,
          plan_id: selectedPlan
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al registrarse');
      }
      
      toast.success('¡Te has unido a la lista de espera!');
      setWaitlistOpen(false);
      loadWaitlistCounts();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const plans = [
    {
      id: 'free',
      name: 'Gratuito',
      price: 0,
      description: 'Publica y vende sin limites',
      highlight: null,
      available: true,
      features: [
        { text: 'Anuncios ilimitados', included: true },
        { text: 'Fotos ilimitadas', included: true },
        { text: 'Perfil basico', included: true },
        { text: 'Mensajeria directa', included: true },
        { text: 'Recepcionista IA', included: false },
        { text: 'Compra Protegida', included: false },
      ],
      cta: 'Comenzar gratis',
      popular: false
    },
    {
      id: 'basic',
      name: 'Basic',
      price: 99,
      description: 'IA y herramientas para crecer',
      highlight: 'Recomendado',
      available: true,
      features: [
        { text: 'Anuncios ilimitados', included: true },
        { text: 'Fotos ilimitadas', included: true },
        { text: 'Perfil profesional + insignia', included: true },
        { text: 'Mejor posicionamiento', included: true },
        { text: 'Recepcionista IA Lite (100/mes)', included: true },
        { text: 'Estadisticas basicas', included: true },
        { text: '1 boost al mes', included: true },
        { text: '1 miembro de equipo', included: true },
      ],
      cta: 'Suscribirse',
      popular: true
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 399,
      description: 'Para criadores profesionales',
      highlight: 'Próximamente',
      available: false,
      features: [
        { text: 'Anuncios ilimitados', included: true },
        { text: 'Perfil destacado premium', included: true },
        { text: 'Recepcionista IA completa', included: true },
        { text: 'Memoria IA supervisada', included: true },
        { text: 'Estadísticas avanzadas', included: true },
        { text: '5 boosts al mes', included: true },
        { text: '5 miembros de equipo', included: true },
        { text: 'Escalamiento a humano', included: true },
      ],
      cta: 'Unirse a waitlist',
      popular: false,
      waitlist: true
    },
    {
      id: 'elite',
      name: 'Elite Breeder',
      price: 999,
      description: 'El máximo para tu criadero',
      highlight: 'Próximamente',
      available: false,
      features: [
        { text: 'Todo de Premium', included: true },
        { text: 'Storefront personalizado', included: true },
        { text: 'Módulos genéticos', included: true },
        { text: 'IA avanzada con RAG', included: true },
        { text: 'Team members ilimitados', included: true },
        { text: 'Boosts ilimitados', included: true },
        { text: 'Soporte prioritario', included: true },
        { text: 'API acceso', included: true },
      ],
      cta: 'Unirse a waitlist',
      popular: false,
      waitlist: true
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0C] pt-24 pb-16" data-testid="pricing-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-zinc-400 mb-6">
            <Zap className="w-4 h-4 text-emerald-400" />
            Sin comisiones por venta — paga solo por tu plan
          </span>
          <h1 className="text-4xl md:text-5xl font-light tracking-tight text-zinc-100 mb-4 font-[Outfit]">
            Invierte en tu criadero
          </h1>
          <p className="text-lg text-zinc-500 max-w-2xl mx-auto">
            Elige el plan que impulse tu negocio. Automatiza tu atención, 
            destaca tus anuncios y vende más. Cancela cuando quieras.
          </p>
        </div>

        {/* Coupon Section */}
        <div className="max-w-md mx-auto mb-10" data-testid="coupon-section">
          <div className="glass-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-medium text-zinc-300">Tienes un cupón de descuento?</span>
            </div>
            <div className="flex gap-2">
              <Input
                value={couponCode}
                onChange={(e) => {
                  setCouponCode(e.target.value.toUpperCase());
                  if (couponValid) setCouponValid(null);
                }}
                placeholder="Ej: MORPHMX20"
                className="bg-white/5 border-white/10 text-zinc-100 uppercase"
                data-testid="coupon-code-input"
              />
              <Button
                onClick={handleValidateCoupon}
                disabled={!couponCode.trim() || couponLoading}
                variant="outline"
                className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 whitespace-nowrap"
                data-testid="validate-coupon-btn"
              >
                {couponLoading ? 'Validando...' : 'Aplicar'}
              </Button>
            </div>
            {couponValid && (
              <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20" data-testid="coupon-valid-badge">
                <Check className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-emerald-300">{couponValid.name}</span>
                <span className="text-xs text-emerald-400/70 ml-auto">
                  {couponValid.percent_off ? `${couponValid.percent_off}% off` : `- $${couponValid.amount_off} MXN`}
                </span>
                <button onClick={() => { setCouponValid(null); setCouponCode(''); }} className="text-zinc-500 hover:text-zinc-300 ml-1">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const isCurrentPlan = user?.subscription === plan.id;
            const waitlistCount = waitlistCounts[plan.id] || 0;
            
            return (
              <div 
                key={plan.id}
                className={`relative glass-card p-6 flex flex-col ${
                  plan.popular ? 'border-emerald-500/30 ring-1 ring-emerald-500/20' : ''
                } ${!plan.available ? 'opacity-90' : ''}`}
              >
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                      plan.popular 
                        ? 'bg-emerald-600 text-white' 
                        : 'bg-purple-600/20 text-purple-300 border border-purple-500/30'
                    }`}>
                      {plan.highlight}
                    </span>
                  </div>
                )}

                <div className="mb-4 mt-2">
                  <h3 className="text-xl font-medium text-zinc-100 mb-1 font-[Outfit]">{plan.name}</h3>
                  <p className="text-sm text-zinc-500">{plan.description}</p>
                </div>

                <div className="mb-6">
                  {couponValid && plan.available && plan.id !== 'free' && getDiscountedPrice(plan.price) !== null ? (
                    <div>
                      <span className="text-lg text-zinc-500 line-through mr-2">
                        ${plan.price.toLocaleString()}
                      </span>
                      <span className="text-4xl font-semibold text-emerald-400 font-[Outfit]">
                        ${getDiscountedPrice(plan.price).toLocaleString()}
                      </span>
                      <span className="text-zinc-500 ml-1">MXN/1er mes</span>
                    </div>
                  ) : (
                    <div>
                      <span className="text-4xl font-semibold text-zinc-100 font-[Outfit]">
                        ${plan.price.toLocaleString()}
                      </span>
                      <span className="text-zinc-500 ml-1">MXN/mes</span>
                    </div>
                  )}
                </div>

                <ul className="space-y-2.5 mb-6 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature.text} className="flex items-start gap-2">
                      {feature.included ? (
                        <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                      ) : (
                        <X className="w-4 h-4 text-zinc-600 flex-shrink-0 mt-0.5" />
                      )}
                      <span className={`text-sm ${feature.included ? 'text-zinc-300' : 'text-zinc-600'}`}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                {isCurrentPlan ? (
                  <Button disabled className="w-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                    <Check className="w-4 h-4 mr-2" />
                    Plan actual
                  </Button>
                ) : plan.waitlist ? (
                  <div>
                    <Button 
                      onClick={() => openWaitlist(plan.id)}
                      variant="outline" 
                      className="w-full border-purple-500/30 text-purple-300 hover:bg-purple-500/10"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {plan.cta}
                    </Button>
                    {waitlistCount > 0 && (
                      <p className="text-center text-xs text-zinc-500 mt-2">
                        {waitlistCount} criadores esperando
                      </p>
                    )}
                  </div>
                ) : plan.id === 'free' ? (
                  <Link to="/registro?role=seller">
                    <Button variant="outline" className="w-full border-white/10 text-zinc-300 hover:bg-white/5">
                      {plan.cta}
                    </Button>
                  </Link>
                ) : (
                  <Button 
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={loading === plan.id}
                    className={`w-full ${
                      plan.popular 
                        ? 'bg-emerald-600 hover:bg-emerald-500 text-white' 
                        : 'bg-zinc-100 text-zinc-900 hover:bg-white'
                    }`}
                  >
                    {loading === plan.id ? 'Procesando...' : plan.cta}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Value Props */}
        <div className="mt-20">
          <h2 className="text-2xl font-light tracking-tight text-zinc-100 mb-8 text-center font-[Outfit]">
            ¿Por qué pagar por MorphMX?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="glass-card p-6 text-center">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-4 mx-auto">
                <Bot className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-lg font-medium text-zinc-100 mb-2">Vende mientras duermes</h3>
              <p className="text-sm text-zinc-500">
                Tu Recepcionista IA responde preguntas 24/7: horarios, precios, envíos, apartados. 
                Tus clientes obtienen respuestas inmediatas.
              </p>
            </div>
            <div className="glass-card p-6 text-center">
              <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center mb-4 mx-auto">
                <TrendingUp className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg font-medium text-zinc-100 mb-2">Más visibilidad</h3>
              <p className="text-sm text-zinc-500">
                Los vendedores Basic y Premium aparecen primero en búsquedas. 
                Más visitas = más ventas potenciales.
              </p>
            </div>
            <div className="glass-card p-6 text-center">
              <div className="w-12 h-12 rounded-2xl bg-purple-500/10 flex items-center justify-center mb-4 mx-auto">
                <Shield className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-lg font-medium text-zinc-100 mb-2">Confianza del comprador</h3>
              <p className="text-sm text-zinc-500">
                La insignia de vendedor activo genera confianza. 
                Los compradores prefieren criadores comprometidos con la plataforma.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Simple */}
        <div className="mt-16 max-w-2xl mx-auto">
          <h3 className="text-xl font-medium text-zinc-100 mb-6 text-center">Preguntas frecuentes</h3>
          <div className="space-y-4">
            <div className="glass-card p-4">
              <p className="font-medium text-zinc-100 mb-1">¿Puedo cancelar en cualquier momento?</p>
              <p className="text-sm text-zinc-500">Sí, sin compromisos. Cancela desde tu dashboard cuando quieras.</p>
            </div>
            <div className="glass-card p-4">
              <p className="font-medium text-zinc-100 mb-1">¿Cobran comisión por venta?</p>
              <p className="text-sm text-zinc-500">No. Pagas solo tu suscripción mensual. Tus ventas son 100% tuyas.</p>
            </div>
            <div className="glass-card p-4">
              <p className="font-medium text-zinc-100 mb-1">¿Qué es la Recepcionista IA Lite?</p>
              <p className="text-sm text-zinc-500">Responde automáticamente FAQs, horarios, métodos de pago y opciones de entrega. 100 respuestas al mes incluidas.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Waitlist Modal */}
      <Dialog open={waitlistOpen} onOpenChange={setWaitlistOpen}>
        <DialogContent className="bg-[#121215] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">
              Únete a la lista de espera - {selectedPlan === 'premium' ? 'Premium' : 'Elite Breeder'}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Te notificaremos cuando este plan esté disponible. Sé de los primeros en acceder.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-400 mb-2 block">Email *</Label>
              <Input
                type="email"
                value={waitlistForm.email}
                onChange={(e) => setWaitlistForm({ ...waitlistForm, email: e.target.value })}
                placeholder="tu@email.com"
                className="bg-white/5 border-white/10 text-zinc-100"
              />
            </div>
            <div>
              <Label className="text-zinc-400 mb-2 block">Nombre de tu criadero</Label>
              <Input
                value={waitlistForm.breeder_name}
                onChange={(e) => setWaitlistForm({ ...waitlistForm, breeder_name: e.target.value })}
                placeholder="Ej: Reptiles MX"
                className="bg-white/5 border-white/10 text-zinc-100"
              />
            </div>
            <div>
              <Label className="text-zinc-400 mb-2 block">¿Cuántos anuncios tienes actualmente?</Label>
              <Input
                type="number"
                value={waitlistForm.current_listings}
                onChange={(e) => setWaitlistForm({ ...waitlistForm, current_listings: parseInt(e.target.value) || 0 })}
                className="bg-white/5 border-white/10 text-zinc-100"
              />
            </div>
            <div>
              <Label className="text-zinc-400 mb-2 block">¿Qué esperas de la Recepcionista IA?</Label>
              <Textarea
                value={waitlistForm.ai_interest}
                onChange={(e) => setWaitlistForm({ ...waitlistForm, ai_interest: e.target.value })}
                placeholder="Cuéntanos cómo te ayudaría la IA en tu criadero..."
                className="bg-white/5 border-white/10 text-zinc-100"
                rows={3}
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setWaitlistOpen(false)} className="flex-1 border-white/10 text-zinc-400">
              Cancelar
            </Button>
            <Button onClick={handleWaitlistSubmit} className="flex-1 bg-purple-600 hover:bg-purple-500">
              Unirme a la lista
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
